
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Users, Mail, Phone, Building2, User, UserCheck, FileText, Calendar, MapPin, Edit, Archive, RotateCcw, Trash2, Skull, CheckCircle, XCircle } from "lucide-react";
import { useState } from "react";
import { useContactDeals, useOrganizationDeals } from "@/hooks/use-contact-deals";
import { usePartnerOwnedContactDeals } from "@/hooks/use-partner-owned-contact-deals";
import { usePartnerCrmAccess } from "@/hooks/use-partner-crm-access";
import { useActivitiesByContact } from "@/hooks/use-activities";
import { useDeleteContact, useUpdateContact } from "@/hooks/use-contacts";
import { useArchiveUser } from "@/hooks/use-archive-user";
import { useSimpleRole } from "@/hooks/use-simple-role";
import { ContactDealsList } from "./ContactDealsList";
import { ActivitiesTab } from "./ActivitiesTab";
import { ContactUserBridge } from "./ContactUserBridge";
import { PermanentDeleteModal } from "./PermanentDeleteModal";
import { useToast } from "@/hooks/use-toast";

interface ContactDetailModalProps {
  contact: any;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onEdit?: (contact: any) => void;
}

export const ContactDetailModal = ({ contact, open, onOpenChange, onEdit }: ContactDetailModalProps) => {
  const [archiveReason, setArchiveReason] = useState("");
  const [permanentDeleteOpen, setPermanentDeleteOpen] = useState(false);

  // Check if current user is a partner
  const { data: isPartner } = usePartnerCrmAccess();
  const { role: userRole } = useSimpleRole();
  const deleteContact = useDeleteContact();
  const updateContact = useUpdateContact();
  const { archiveUser, restoreUser } = useArchiveUser();
  const { toast } = useToast();
  
  const canArchive = userRole === 'superuser' || userRole === 'admin';
  const canPermanentlyDelete = userRole === 'superuser';
  const canApproveOwnership = userRole === 'superuser';
  const isArchived = contact.archived === true;
  const hasUserAccount = !!contact.linked_user?.id;
  const isPendingApproval = contact.partner_ownership_status === 'pending';
  
  // Fetch deals based on user role
  const { data: contactDeals = [], isLoading: contactDealsLoading } = useContactDeals(contact.id);
  const { data: orgDeals = [], isLoading: orgDealsLoading } = useOrganizationDeals(
    contact.organization_id || ""
  );
  
  // For partners, also fetch deals from contacts they own
  const { data: partnerOwnedDeals = [], isLoading: partnerDealsLoading } = usePartnerOwnedContactDeals(
    contact?.external_owner_id || ''
  );
  
  const { data: activities = [], isLoading: activitiesLoading } = useActivitiesByContact(contact.id);

  const getContactInitials = () => {
    return `${contact.first_name?.[0] || ''}${contact.last_name?.[0] || ''}`.toUpperCase();
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString();
  };

  const getOwnerInfo = () => {
    if (contact.internal_owner) {
      return `${contact.internal_owner.first_name} ${contact.internal_owner.last_name} (Staff)`;
    }
    if (contact.external_owner) {
      return `${contact.external_owner.first_name} ${contact.external_owner.last_name} (Partner)`;
    }
    return null;
  };

  const handleEdit = () => {
    onEdit?.(contact);
    onOpenChange(false);
  };

  const handleDelete = () => {
    if (window.confirm(`Are you sure you want to delete ${contact.first_name} ${contact.last_name}?`)) {
      deleteContact.mutate(contact.id);
      onOpenChange(false);
    }
  };

  const handleArchive = () => {
    archiveUser.mutate({
      contactId: contact.id,
      userId: contact.linked_user?.id || null,
      reason: archiveReason || undefined
    });
    setArchiveReason("");
    onOpenChange(false);
  };

  const handleRestore = () => {
    restoreUser.mutate({
      contactId: contact.id,
      userId: contact.linked_user?.id || null
    });
  };

  // Combine contact, organization, and partner-owned deals, removing duplicates
  const allDeals = [
    ...contactDeals, 
    ...orgDeals.filter(orgDeal => !contactDeals.some(contactDeal => contactDeal.id === orgDeal.id)),
    ...(isPartner ? partnerOwnedDeals.filter(deal => 
      deal.contact_id === contact?.id && 
      !contactDeals.some(contactDeal => contactDeal.id === deal.id) &&
      !orgDeals.some(orgDeal => orgDeal.id === deal.id)
    ) : [])
  ];

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader className="bg-white -mx-6 -mt-6 px-6 py-4 rounded-t-xl border-b border-slate-200">
            <div className="flex items-center gap-4">
              {contact.profile_image || contact.avatar_url ? (
                <img
                  src={contact.profile_image || contact.avatar_url}
                  alt={`${contact.first_name} ${contact.last_name}`}
                  className="w-16 h-16 rounded-full object-cover border shadow-sm ring-1"
                  style={{ borderColor: "black", boxShadow: "0 0 0 1px black" }}
                />
              ) : (
                <div className="w-16 h-16 rounded-full bg-white flex items-center justify-center border shadow-sm ring-1" style={{ borderColor: "black", boxShadow: "0 0 0 1px black" }}>
                  <span className="text-black font-bold text-lg">{getContactInitials()}</span>
                </div>
              )}
              <div className="flex-1">
                <DialogTitle className="text-black text-xl font-semibold">
                  {contact.first_name} {contact.last_name}
                </DialogTitle>
                <div className="flex items-center gap-2 mt-1">
                  <Badge 
                    className="text-xs font-semibold px-3 py-1 rounded-full shadow-sm text-black"
                    style={{ backgroundColor: "white" }}
                  >
                    {contact.contact_type}
                  </Badge>
                  {contact.has_user_account && (
                    <Badge 
                      className="text-xs font-semibold cursor-default text-black"
                      style={{ backgroundColor: "#DFEF97", borderColor: "black" }}
                    >
                      <UserCheck className="h-3 w-3 mr-1" />
                      Active User
                    </Badge>
                  )}
                    {isArchived && (
                    <Badge className="text-xs font-semibold px-3 py-1 rounded-full bg-slate-500 text-white">
                      <Archive className="h-3 w-3 mr-1" />
                      Archived
                    </Badge>
                  )}
                  {isPendingApproval && (
                    <Badge className="text-xs font-semibold px-3 py-1 rounded-full bg-amber-100 text-amber-800 border border-amber-300">
                      Pending Approval
                    </Badge>
                  )}
                </div>
              </div>
              
              {/* Action buttons in modal header */}
              <div className="flex items-center gap-1">
                {onEdit && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleEdit}
                    className="hover:bg-slate-50 hover:text-slate-700"
                    title="Edit Contact"
                  >
                    <Edit className="h-4 w-4" />
                  </Button>
                )}
                
                {canArchive && (
                  isArchived ? (
                    <>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={handleRestore}
                        className="hover:bg-green-50 hover:text-green-600"
                        disabled={restoreUser.isPending}
                        title="Restore Contact"
                      >
                        <RotateCcw className="h-4 w-4" />
                      </Button>
                      
                      {canPermanentlyDelete && hasUserAccount && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setPermanentDeleteOpen(true)}
                          className="hover:bg-red-100 hover:text-red-700"
                          title="Permanently Delete User Data"
                        >
                          <Skull className="h-4 w-4" />
                        </Button>
                      )}
                    </>
                  ) : (
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="hover:bg-amber-50 hover:text-amber-600"
                          title="Archive Contact"
                        >
                          <Archive className="h-4 w-4" />
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Archive Contact</AlertDialogTitle>
                          <AlertDialogDescription>
                            Are you sure you want to archive {contact.first_name} {contact.last_name}?
                            {contact.has_user_account && (
                              <span className="block mt-2 text-amber-600 font-medium">
                                This user has an active account. Archiving will disable their login access.
                              </span>
                            )}
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <div className="py-2">
                          <Textarea
                            placeholder="Optional: Reason for archiving..."
                            value={archiveReason}
                            onChange={(e) => setArchiveReason(e.target.value)}
                            className="resize-none"
                            rows={2}
                          />
                        </div>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Cancel</AlertDialogCancel>
                          <AlertDialogAction 
                            onClick={handleArchive}
                            disabled={archiveUser.isPending}
                            className="bg-amber-600 hover:bg-amber-700"
                          >
                            {archiveUser.isPending ? "Archiving..." : "Archive"}
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  )
                )}
                
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleDelete}
                  className="hover:bg-red-50 hover:text-red-600"
                  disabled={deleteContact.isPending}
                  title="Delete Contact"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </DialogHeader>

          <Tabs defaultValue="details" className="flex-1 flex flex-col overflow-hidden">
            <TabsList className="grid w-full grid-cols-3">
              <TabsTrigger value="details">Contact Details</TabsTrigger>
              <TabsTrigger value="deals" className="flex items-center gap-2">
                <FileText className="h-4 w-4" />
                Deals ({allDeals.length})
              </TabsTrigger>
              <TabsTrigger value="activities" className="flex items-center gap-2">
                <Calendar className="h-4 w-4" />
                Activities ({activities.length})
              </TabsTrigger>
            </TabsList>
            
            <TabsContent value="details" className="flex-1 overflow-auto min-h-[400px]">
              <div className="mt-6 space-y-6 p-6">
                {/* Partner Ownership Approval Banner */}
                {isPendingApproval && canApproveOwnership && (
                  <Card className="bg-amber-50 border-amber-200 rounded-xl shadow-sm">
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm font-medium text-amber-800">
                            Partner ownership pending approval
                          </p>
                          <p className="text-xs text-amber-600 mt-1">
                            A partner has claimed ownership of this contact. Review and approve or reject.
                          </p>
                        </div>
                        <div className="flex items-center gap-2">
                          <Button
                            size="sm"
                            onClick={() => {
                              updateContact.mutate({ id: contact.id, data: { partner_ownership_status: 'approved' } as any });
                              toast({ title: "Approved", description: "Partner ownership approved." });
                            }}
                            className="bg-green-600 hover:bg-green-700 text-white"
                            disabled={updateContact.isPending}
                          >
                            <CheckCircle className="h-4 w-4 mr-1" />
                            Approve
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => {
                              updateContact.mutate({ id: contact.id, data: { partner_ownership_status: 'rejected', external_owner_id: null } as any });
                              toast({ title: "Rejected", description: "Partner ownership rejected and cleared." });
                            }}
                            className="border-red-300 text-red-700 hover:bg-red-50"
                            disabled={updateContact.isPending}
                          >
                            <XCircle className="h-4 w-4 mr-1" />
                            Reject
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Contact Information */}
                <Card className="bg-white border-slate-200 rounded-xl shadow-sm">
                  <CardHeader>
                    <CardTitle className="text-slate-800 flex items-center gap-2">
                      <Users className="h-5 w-5" />
                      Contact Information
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="flex items-center gap-2">
                        <Mail className="h-4 w-4 text-slate-600" />
                        <span className="text-sm text-slate-700">{contact.email}</span>
                      </div>
                      {contact.phone && (
                        <div className="flex items-center gap-2">
                          <Phone className="h-4 w-4 text-slate-600" />
                          <span className="text-sm text-slate-700">{contact.phone}</span>
                        </div>
                      )}
                    </div>
                    
                    {contact.job_title && (
                      <div>
                        <span className="text-sm font-medium text-slate-700">Job Title:</span>
                        <span className="text-sm text-slate-600 ml-2">{contact.job_title}</span>
                      </div>
                    )}

                    {contact.country && (
                      <div className="flex items-center gap-2">
                        <MapPin className="h-4 w-4 text-slate-600" />
                        <span className="text-sm text-slate-700">{contact.country}</span>
                      </div>
                    )}

                    {contact.organizations && (
                      <div className="flex items-center gap-2">
                        <Building2 className="h-4 w-4 text-slate-600" />
                        <span className="text-sm text-slate-700">{contact.organizations.name}</span>
                      </div>
                    )}

                    {getOwnerInfo() && (
                      <div className="flex items-center gap-2">
                        <User className="h-4 w-4 text-slate-600" />
                        <span className="text-sm text-slate-700">
                          Owner: {getOwnerInfo()}
                        </span>
                      </div>
                    )}

                    {contact.notes && (
                      <div className="border-t border-slate-200 pt-3">
                        <span className="text-sm font-medium text-slate-700">Notes:</span>
                        <p className="text-sm text-slate-600 mt-1">{contact.notes}</p>
                      </div>
                    )}

                    {contact.has_user_account && contact.linked_user ? (
                      <div className="border border-green-200 bg-green-50 rounded-lg p-3">
                        <div className="flex items-center gap-2 mb-2">
                          <UserCheck className="h-4 w-4 text-green-600" />
                          <span className="text-green-600 font-medium text-sm">Active User Account</span>
                        </div>
                        <div className="text-xs text-green-600">
                          <span className="font-medium">User ID:</span>
                          <code className="bg-white px-2 py-1 rounded text-xs ml-2 font-mono">
                            {contact.linked_user.id}
                          </code>
                        </div>
                      </div>
                    ) : (
                      <div className="border border-slate-200 bg-slate-50 rounded-lg p-3">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <User className="h-4 w-4 text-slate-500" />
                            <span className="text-slate-500 text-sm">No user account</span>
                          </div>
                          <ContactUserBridge contact={contact} />
                        </div>
                      </div>
                    )}

                    <div className="pt-2 border-t border-slate-200 flex items-center gap-2">
                      <Calendar className="h-4 w-4 text-slate-400" />
                      <span className="text-xs text-slate-500">
                        Created: {formatDate(contact.created_at)}
                      </span>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </TabsContent>
            
            <TabsContent value="deals" className="flex-1 overflow-auto min-h-[400px]">
              <div className="p-6">
                <ContactDealsList 
                  deals={allDeals}
                  isLoading={contactDealsLoading || orgDealsLoading || (isPartner && partnerDealsLoading)}
                  title="Related Deals"
                  showAssignee={true}
                />
              </div>
            </TabsContent>
            
            <TabsContent value="activities" className="flex-1 overflow-auto min-h-[400px]">
              <ActivitiesTab 
                activities={activities}
                isLoading={activitiesLoading}
                contactId={contact.id}
                title="Contact Activities"
              />
            </TabsContent>
          </Tabs>
        </DialogContent>
      </Dialog>

      {canPermanentlyDelete && hasUserAccount && (
        <PermanentDeleteModal
          open={permanentDeleteOpen}
          onOpenChange={setPermanentDeleteOpen}
          contact={contact}
        />
      )}
    </>
  );
};
